import { createErsClient, idempotencyMiddleware } from '@ers/sdk';
import { ApiError } from './errors';

/**
 * Клієнт API для вебзастосунку: сеанс через cookie, заголовок X-ERS-Client: web,
 * ключ ідемпотентності для кожного POST. Адреса відносна — Vite проксіює /api на сервер.
 */
export const api = createErsClient({ web: true });
api.use(idempotencyMiddleware);

// ——— Офлайн (сервіс-воркер) ———

let offlineStamp: string | null = null;
const offlineListeners = new Set<() => void>();

/** Коли показано збережені дані (сервіс-воркер відповів із кешу) — час їх отримання; null — дані свіжі. */
export function getOfflineStamp() {
  return offlineStamp;
}
export function subscribeOfflineStamp(cb: () => void) {
  offlineListeners.add(cb);
  return () => {
    offlineListeners.delete(cb);
  };
}
function noteResponse(res: Response) {
  const next = res.headers.get('x-ers-offline') ? res.headers.get('x-ers-cached-at') : null;
  if (next !== offlineStamp && (next || offlineStamp)) {
    offlineStamp = next;
    offlineListeners.forEach((l) => l());
  }
}

/** Дію збережено в офлайн-черзі сервіс-воркера (202 + X-ERS-Queued) — вона ще не виконана на сервері. */
export function queuedError(): ApiError {
  return new ApiError({
    status: 202,
    code: 'queued_offline',
    title: 'Збережено офлайн',
    detail: 'Немає мережі: дію збережено й буде надіслано автоматично, щойно з’явиться зв’язок.',
  });
}

api.use({
  onResponse({ response }) {
    noteResponse(response);
    if (response.headers.get('x-ers-queued') === '1') throw queuedError();
    return undefined;
  },
});

type FetchResult<T> = { data?: T; error?: unknown; response: Response };

/** Розгортає відповідь openapi-fetch: дані або ApiError (problem+json). */
export async function call<T>(promise: Promise<FetchResult<T>>): Promise<NonNullable<T>> {
  let res: FetchResult<T>;
  try {
    res = await promise;
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    if (e instanceof ApiError) throw e;
    throw ApiError.network();
  }
  if (!res.response.ok || res.error !== undefined) {
    const err = ApiError.fromResponse(res.response, res.error);
    notifyUnauthorized(err);
    throw err;
  }
  return res.data as NonNullable<T>;
}

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** Підписка на 401: сеанс завершився — оболонка переводить на сторінку входу. */
export function onUnauthorized(cb: UnauthorizedListener) {
  unauthorizedListeners.add(cb);
  return () => {
    unauthorizedListeners.delete(cb);
  };
}

function notifyUnauthorized(err: ApiError) {
  if (err.status === 401) for (const cb of unauthorizedListeners) cb();
}

/** Значення ETag для заголовка If-Match: W/"версія". */
export function ifMatch(version: number): Record<string, string> {
  return { 'If-Match': `W/"${version}"` };
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Запит до операції, якої ще немає в типах SDK (модулі хвилі 2, нові маршрути).
 * Той самий сеанс (cookie), заголовок X-ERS-Client: web, ключ ідемпотентності для POST; помилки — ApiError.
 * Відсутній маршрут дає ApiError зі status 404 — викликач сам вирішує, чи це «модуль ще не підключено».
 */
export async function apiJson<T>(method: Method, path: string, body?: unknown, init?: { headers?: Record<string, string>; signal?: AbortSignal }): Promise<T> {
  const headers: Record<string, string> = { 'x-ers-client': 'web', accept: 'application/json', ...init?.headers };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method === 'POST' && !headers['idempotency-key']) headers['idempotency-key'] = crypto.randomUUID();
  let res: Response;
  try {
    res = await fetch(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: 'include', signal: init?.signal });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw ApiError.network();
  }
  noteResponse(res);
  if (res.headers.get('x-ers-queued') === '1') throw queuedError();
  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = undefined;
    }
  }
  if (!res.ok) {
    const err = ApiError.fromResponse(res, data);
    notifyUnauthorized(err);
    throw err;
  }
  return data as T;
}

/** Маршрут відсутній на сервері (модуль ще не підключено) — показуємо порожній стан, а не помилку. */
export function isMissingRoute(e: unknown): boolean {
  return e instanceof ApiError && e.status === 404 && (e.code === 'not_found' || e.code === 'http_404') && /Маршрут|не існує/i.test(e.detail);
}
